import utils from "./utils";

export default {
  async created() {
    if (this.autosuggest.isStaticSearch) {
      this.results = { ...this.$root.autosuggestData };
    }
  },
  data() {
    return {
      channelSearch: "",
      minCharSearch: 5,
      results: {},
      timer: 0,
    };
  },
  watch: {
    "inputQuery": function () {
      clearTimeout(this.timer);
      this.timer = setTimeout(
        function () {
          this.updateResults();
        }.bind(this),
        250
      );
    },
    "$root.autosuggestData": function (item) {
      if (this.autosuggest.isStaticSearch) {
        this.results = { ...item };
      }
    },
  },
  computed: {
    filteredResults() {
      const filtered = {};
      Object.keys(this.results).forEach(key => {
        filtered[key] = this.filterResults(this.inputQuery, this.results[key]);
      });
      return filtered;
    },
  },
  methods: {
    /**
     * Extract autosuggestUrl object for the given channel
     * @param name: channel name
     */
    channelConfig(name) {
      return this.autosuggest.autosuggestUrl.filter(channelConfig => channelConfig.channel === name)[0];
    },
    /**
     * Update results list by fetching data for each channel in this.autosuggest.autosuggestUrl.
     *
     * Results are merged by key to provide a single list of results for each type.
     * Result label is used as unique identifier for merging.
     *
     * The list of results for each key is sorted according to the
     * `this.autosuggest.resourceTypesOrder` parameter, if set.
     *
     */
    async updateResults() {
      if (!this.autosuggest.isStaticSearch && this.inputQuery.length >= this.minCharSearch) {
        await this.autosuggest.autosuggestUrl.map(async (channelConfig, index) => {
          await this.fetchResults(channelConfig.url, channelConfig.channel).then(data => {
            if (index === 0) {
              this.results = {};
            }
            Object.keys(data).forEach(key => {
              this.$set(
                this.results,
                key,
                utils.sortArrayByChannel(
                  utils.mergeArrays(this.results[key], data[key], "label"),
                  this.autosuggest.resourceTypesOrder
                )
              );
            });
          });
        });
      }
    },
    /**
     * Fetch search results from URL by given channel.
     *
     * Current grade is injected when needed.
     * In case of grouped resources, they group result is tagged with the calling channel needed by filterResults
     *
     * @param searchUrl: url to query for results
     * @param channelName: name of the channel to assign to results
     */
    async fetchResults(searchUrl, channelName) {
      const url = new URL(searchUrl);
      const params = {
        q: this.inputQuery,
      };
      if (this.filterByGrade) {
        params.grade = this.targetInfo && this.targetInfo.target_grade ? this.targetInfo.target_grade : "";
      }

      Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
      const data = await fetch(url)
        .then(res => res.json())
        .catch(err => {
          // eslint-disable-next-line no-console
          console.log("Search results", err);
          return {};
        });
      if (data.results) {
        return { results: data.results };
      }
      Object.keys(data).forEach(key => {
        // The following check is to detect grouped resources (the only with count attribute > 1)
        // We set channelName only for the first item because for grouped resources we only have one result
        if (data[key].length === 1 && data[key][0].count > 1 && channelName) data[key][0].channel = channelName;
      });
      return data;
    },
    /**
     * Process the results list to display matching text characters.
     *
     * In case of static autosuggest search, filtering is also applied here to the base list of items
     *
     * @param query: user text query
     * @param list: list of items to filter / highlight
     */
    filterResults(query, list) {
      if (query === "") {
        return this.autosuggest.isStaticSearch ? [] : list;
      }

      function contains(value) {
        if (value.label !== undefined) {
          return value.label.toLowerCase().indexOf(query.toLowerCase()) > -1;
        }
        return false;
      }

      const filtered = this.autosuggest.isStaticSearch ? list.filter(contains) : list;
      const regEx = new RegExp(query, "ig");

      filtered.forEach(element => {
        // elements coming from endpoints different than autocomplete are not highlighted
        if (element.href || element.query) {
          // backend only provide the querystring as href attribute because it cannot determine the real URL
          if (element.href && !element.href.startsWith("/") && !element.href.startsWith("http")) {
            // eslint-disable-next-line
            element.href = `${this.autosuggest.searchUrl}?${element.href}`;
          } else if (element.query) {
            // eslint-disable-next-line
            element.href = `${this.autosuggest.searchUrl}?${element.query}`;
          }
          // element.count > 1 means backend has grouped resources, in this case we create a custom
          // label according to the configuration and the count reported by the backend
          if (element.count > 1 && element.channel) {
            // eslint-disable-next-line no-param-reassign
            element.resultString = this.channelConfig(element.channel).label.replace("%(count)s", element.count);
          } else {
            // in case single resources are reported, we wrap the user query with highlight markup
            // regular expressions are used for case insensitive search, the original text is used for display anyway
            // eslint-disable-next-line no-param-reassign
            element.resultString = element.label.replace(
              regEx,
              "<span class='dea-autosuggest-result dea-autosuggest-result--highlight'>$&</span>"
            );
          }
        }
      });
      return filtered;
    },
  },
};
